import { useEffect, useId, useRef, useState } from 'react';

/**
 * A two-series area chart, as inline SVG. No library.
 *
 * ## Why there is no chart library here
 *
 * The bundle. This app is one stylesheet and no Tailwind for the same reason:
 * the smallest usable charting library is bigger than everything the Wallet page
 * ships today, to draw one line. And a library's default look is its own design
 * system, which would put a second palette and a second type scale on a page
 * whose whole point is that there is only one — every colour below is a token
 * from `styles.css`.
 *
 * ## What it draws, and what it refuses to
 *
 * A filled area with a line on top for the primary series, and a dashed line for
 * the comparison one. NO axis library, no smoothing: the points are joined with
 * straight segments, because a spline through daily money invents values between
 * the days that nobody earned. The y-axis is one label — the top of the DATA —
 * rather than five gridlines' worth of numbers, because the question this chart
 * answers is "which days were good", and the exact figure comes from the
 * read-out.
 *
 * ## An empty range still draws
 *
 * THE CHART ALWAYS RENDERS. It used to swap itself for an empty state when
 * every value in both series was zero, and that is why the first creator on the
 * live pilot opened their wallet and found no chart at all: day one is exactly
 * the state where the picture is worth the most, because a flat zero line
 * across a labelled axis is how somebody learns what this chart will show them
 * once they have earned something. The zero range gets the line, the axis dates
 * and a `$0.00` scale label; the sentence explaining it goes UNDER the plot as
 * `emptyNote`, beside the picture rather than instead of it.
 *
 * ## How somebody who cannot see it reads it
 *
 * `role="img"` with a written `aria-label`, so a screen reader is told what the
 * picture IS rather than being walked through 90 path commands. Then the plot is
 * focusable and the arrow keys step through the buckets, writing each one into a
 * live region: "12 Sep 2026: $4.20 earned, $0.00 paid out". That is the same
 * read-out the mouse gets on hover, out of the same state, so the two cannot
 * come to say different things. Home and End go to the ends; Escape clears the
 * selection.
 *
 * ## Geometry
 *
 * THE VIEWBOX IS MEASURED, NOT FIXED. It used to be a constant 720x200 scaled
 * to the card by `width: 100%; height: auto`, which is fine while the card is
 * about 720px wide and absurd once it is not: the content column fills the
 * screen now, so on a 2,560px monitor that same box scaled to 624px TALL — a
 * chart the height of the viewport, with a 2px line stretched across it. The
 * plot's own width is observed instead and the viewBox is drawn at 1:1 with
 * CSS pixels, so a 4px dot is 4px at every width, the height follows the width
 * within sane bounds (`heightFor`), and `height: auto` in the stylesheet then
 * resolves to exactly that.
 *
 * `vector-effect: non-scaling-stroke` stays on every stroke: it costs nothing
 * at 1:1 and it is what keeps the line honest in the one frame before the
 * first measurement lands. A single bucket draws as a flat segment across the
 * whole plot rather than a dot nobody can see.
 */

/** The width assumed until the plot has been measured — and in jsdom, where
 *  there is no layout and `getBoundingClientRect` is all zeroes, forever. */
const VIEW_W = 720;

/**
 * How tall the plot is at a given width.
 *
 * Not a fixed aspect ratio, which is what produced the 624px chart: a ratio
 * has no opinion about how tall is too tall. 140px is the least a two-series
 * area chart can be read at; 300px is the most a chart should take of a first
 * viewport when there is a table under it. Between them it grows with the
 * width, so a wide card gets a wide chart and not a tall one.
 */
function heightFor(width: number): number {
  const ideal = Math.min(300, Math.max(140, width * 0.28));
  /* Snapped to 20 view units, which are CSS pixels here. Two reasons: the
     baseline and the padding then land on whole pixels at every width instead
     of on a half one that Chromium anti-aliases into a grey smudge, and the
     unmeasured fallback (720 wide) resolves to exactly the 200 this chart has
     always been — so the one geometry the tests pin does not move. */
  return Math.round(ideal / 20) * 20;
}
/**
 * Room at the top for the tallest bar AND for the y-axis label above it.
 *
 * 24 and not 14, measured: the label is absolutely positioned in the top-right
 * corner and the tallest point can be anywhere, so at 390px a dashed green spike
 * ran straight through "$45.00" and neither the number nor the line could be
 * read. The label also carries the plot's own background now (`.chart-axis-y`),
 * which is the belt to this brace — a chart cannot know that the peak is not in
 * the corner.
 */
const PAD_TOP = 24;
const PAD_BOTTOM = 6;

export interface ChartPoint {
  /** The bucket's key, for React and for the read-out's own label. */
  key: string;
  /** What the axis and the read-out call this bucket. */
  label: string;
  /** The primary series' value, in whatever unit `formatValue` prints. */
  value: number;
  /** The comparison series' value. */
  compare: number;
}

export interface AreaChartProps {
  points: ChartPoint[];
  /** What the two series are called, in the key and in the read-out. */
  valueName: string;
  compareName: string;
  formatValue: (value: number) => string;
  /**
   * The sentence a screen reader is given for the whole picture. Written by the
   * caller, because only the caller knows what the range is called.
   */
  summary: string;
  /**
   * A sentence under the plot for a range with nothing in it.
   *
   * SHOWN BESIDE THE CHART, NEVER INSTEAD OF IT. This prop used to replace the
   * whole plot when every value was zero, which is why a creator with no
   * earnings yet saw no chart at all — the one state where the picture matters
   * most, because a flat line across a labelled axis is how somebody learns the
   * chart exists and what it will show them. See the note above.
   */
  emptyNote?: React.ReactNode;
}

/** The y for a value, in view units. */
function yFor(value: number, max: number, viewH: number): number {
  if (max <= 0) return viewH - PAD_BOTTOM;
  const usable = viewH - PAD_TOP - PAD_BOTTOM;
  return viewH - PAD_BOTTOM - (value / max) * usable;
}

/** The x for bucket `i` of `n`. One bucket sits at the left edge. */
function xFor(index: number, count: number, viewW: number): number {
  if (count <= 1) return 0;
  return (index / (count - 1)) * viewW;
}

/**
 * The plot's own width in CSS pixels, so the viewBox can be drawn at 1:1.
 *
 * `ResizeObserver` and not a resize listener: the card changes width when the
 * drawer opens, when a `.split` collapses at 900px and when the window moves,
 * and only one of those three is a window resize. It does not exist in jsdom,
 * where there is no layout to observe and the fallback width is the right
 * answer — every test renders the same chart it renders today.
 */
function usePlotWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(VIEW_W);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = (): void => {
      /* The CONTENT box: the plot carries 8px of padding down each side and
         the svg is laid out inside it, so the border box would draw every
         point 16px too far to the right. Rounded, because a fractional
         viewBox re-renders the whole path on a sub-pixel zoom change. */
      const style = getComputedStyle(node);
      const inner =
        node.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0');
      if (inner > 0) setWidth(Math.round(inner));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

/**
 * Which buckets get a date under them.
 *
 * Always both ends, then evenly spaced buckets between them — as many as fit
 * at roughly 180px each, which is a short date ("24 Sep 2026") plus the air
 * that keeps two of them from reading as one. At 330px wide that is two
 * labels; at 720 it is the three this chart has always drawn; at 2,200 it is
 * twelve, which is what makes a wide plot readable rather than merely large.
 *
 * Returns the POINTS, not their labels, so React has a stable key per tick.
 */
function axisTicks(points: ChartPoint[], width: number): ChartPoint[] {
  if (points.length === 0) return [];
  if (points.length === 1) return [points[0]!];
  const room = Math.max(2, Math.min(points.length, Math.floor(width / 180)));
  const picked: ChartPoint[] = [];
  for (let i = 0; i < room; i += 1) {
    const at = Math.round((i / (room - 1)) * (points.length - 1));
    /* Two ticks can round to the same bucket on a short series; one date
       printed twice reads as a chart that lost a day. */
    if (picked[picked.length - 1] !== points[at]) picked.push(points[at]!);
  }
  return picked;
}

export function AreaChart({
  points,
  valueName,
  compareName,
  formatValue,
  summary,
  emptyNote,
}: AreaChartProps): React.JSX.Element {
  const gradientId = useId();
  const [plotRef, viewW] = usePlotWidth();
  const viewH = heightFor(viewW);
  /* The bucket the read-out is about: a hover, or an arrow key. One piece of
     state for both, so the mouse and the keyboard cannot disagree. */
  const [active, setActive] = useState<number | null>(null);

  /* The real top of the data, which is what the y-axis is labelled with — and
     it is zero for a range with nothing in it, because "$0.01" over a flat zero
     line would be a scale the chart invented. */
  const peak = points.reduce((top, p) => Math.max(top, p.value, p.compare), 0);
  /* The DIVISOR, which cannot be zero. A flat line along the baseline is the
     right picture for a range with nothing in it; dividing by zero is not. */
  const max = Math.max(peak, 1);
  const anything = peak > 0;

  const count = points.length;
  const line = (pick: (p: ChartPoint) => number): string =>
    points
      .map(
        (p, i) =>
          `${i === 0 ? 'M' : 'L'}${xFor(i, count, viewW).toFixed(1)} ${yFor(pick(p), max, viewH).toFixed(1)}`,
      )
      .join(' ');

  /* The area is the value line, closed along the bottom. With one bucket the
     line is a point, so the path is drawn flat across the plot instead — a
     single day of earnings has to be visible. */
  const flat = (pick: (p: ChartPoint) => number): string =>
    `M0 ${yFor(pick(points[0]!), max, viewH).toFixed(1)} L${viewW} ${yFor(pick(points[0]!), max, viewH).toFixed(1)}`;
  const valueLine = count === 1 ? flat((p) => p.value) : line((p) => p.value);
  const compareLine = count === 1 ? flat((p) => p.compare) : line((p) => p.compare);
  const area = `${valueLine} L${viewW} ${viewH - PAD_BOTTOM} L0 ${viewH - PAD_BOTTOM} Z`;

  const shown = active !== null ? points[active] : undefined;

  /** Which bucket a pointer at this fraction of the width is over. */
  function pick(event: React.MouseEvent<HTMLDivElement>): void {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width <= 0 || count === 0) return;
    const fraction = (event.clientX - box.left) / box.width;
    const index = Math.round(fraction * (count - 1));
    setActive(Math.min(count - 1, Math.max(0, index)));
  }

  function onKey(event: React.KeyboardEvent<HTMLDivElement>): void {
    const step = (delta: number): void => {
      event.preventDefault();
      setActive((current) => {
        const from = current ?? (delta > 0 ? -1 : count);
        return Math.min(count - 1, Math.max(0, from + delta));
      });
    };
    if (event.key === 'ArrowRight') step(1);
    else if (event.key === 'ArrowLeft') step(-1);
    else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(count - 1);
    } else if (event.key === 'Escape') setActive(null);
  }

  return (
    <div className="chart">
      <div
        ref={plotRef}
        className="chart-plot"
        /* `img` and not `application`: what is here is a picture, and the label
           is the sentence that describes it. The arrow keys are the way to read
           the numbers, announced by the live region below. */
        role="img"
        aria-label={summary}
        tabIndex={0}
        onMouseMove={pick}
        onMouseLeave={() => setActive(null)}
        onKeyDown={onKey}
        onBlur={() => setActive(null)}
        data-testid="earnings-chart"
      >
        <svg viewBox={`0 0 ${viewW} ${viewH}`} aria-hidden="true" focusable="false">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              {/* The two stops carry CLASSES rather than literal colours: the
                  fill under the line has to be a blue wash on #050816 and a
                  quieter one on #f4f7fb, and a colour written here is a colour
                  the light theme cannot reach. `stop-color` is set from the
                  tokens in `styles.css`. */}
              <stop offset="0%" className="chart-area-from" />
              <stop offset="100%" className="chart-area-to" />
            </linearGradient>
          </defs>
          {/* The baseline, so a flat stretch reads as zero rather than as the
              bottom of the box. */}
          <line
            className="chart-grid"
            x1="0"
            y1={viewH - PAD_BOTTOM}
            x2={viewW}
            y2={viewH - PAD_BOTTOM}
          />
          <path d={area} fill={`url(#${gradientId})`} />
          <path className="chart-line" d={valueLine} />
          <path className="chart-line paid" d={compareLine} />
          {shown && (
            <>
              <line
                className="chart-guide"
                x1={xFor(active!, count, viewW)}
                y1={PAD_TOP - 8}
                x2={xFor(active!, count, viewW)}
                y2={viewH - PAD_BOTTOM}
              />
              <circle
                className="chart-dot"
                cx={xFor(active!, count, viewW)}
                cy={yFor(shown.value, max, viewH)}
                r="4"
              />
              {shown.compare > 0 && (
                <circle
                  className="chart-dot paid"
                  cx={xFor(active!, count, viewW)}
                  cy={yFor(shown.compare, max, viewH)}
                  r="4"
                />
              )}
            </>
          )}
        </svg>

        {/* The top of the scale, once. Not five gridlines of numbers: the exact
            figure for a bucket is what the read-out is for. */}
        <span className="chart-axis-y">{formatValue(peak)}</span>

        {shown && (
          <span
            className="chart-tip"
            data-testid="chart-readout"
            /* Clamped to 6%..94% so the first and last buckets' boxes stay
               inside the card instead of hanging off its edge. */
            style={{
              left: `${Math.min(94, Math.max(6, count <= 1 ? 50 : (active! / (count - 1)) * 100))}%`,
            }}
          >
            <span className="tip-day">{shown.label}</span>
            <span className="tip-value">
              {formatValue(shown.value)} {valueName}
            </span>
            <span className="tip-value paid">
              {formatValue(shown.compare)} {compareName}
            </span>
          </span>
        )}
      </div>

      {/* The x-axis: the two ends, and as many dates between them as the plot
          is wide enough to set without them running together. Never one per
          bucket — 90 dates is a grey smear at any width — and never three on
          a 2,200px plot either, which is a date, 1,100px of nothing, a date.
          `axisTicks` is the arithmetic; it reads the measured width. */}
      <div className="chart-axis">
        {axisTicks(points, viewW).map((p) => (
          <span key={p.key}>{p.label}</span>
        ))}
      </div>

      <div className="chart-key">
        <span>
          <span className="swatch" aria-hidden="true" />
          {valueName}
        </span>
        <span>
          <span className="swatch paid" aria-hidden="true" />
          {compareName}
        </span>
        <span className="faint">Arrow keys read the days out.</span>
      </div>

      {/* The sentence for an empty range, UNDER the chart the range is empty
          of — so the axis, the dates and the flat line are all still there to
          say what the picture will look like when it is not. */}
      {!anything && emptyNote && <p className="chart-empty-note">{emptyNote}</p>}

      {/* The read-out, again, for a screen reader. `aria-live="polite"` so
          stepping along the chart with the arrow keys speaks each bucket instead
          of silently moving a dot nobody can see. */}
      <p className="sr-only" aria-live="polite">
        {shown
          ? `${shown.label}: ${formatValue(shown.value)} ${valueName}, ${formatValue(shown.compare)} ${compareName}.`
          : ''}
      </p>
    </div>
  );
}
